import React, { useContext, useState } from "react";
import { Button } from "primereact/button";
import * as Yup from "yup";
import Flex from "../../../../components/shared-styles/flexi";
import Input from "../../../../components/form/input";
import { formatErrors, handleInput, handleSubmit } from "../../../../services/utils/processors";
import InputArea from "../../../../components/form/inputArea";
import DatePicker from "../../../../components/form/DatePicker";
import CountryDropdown from "../../../../components/form/country-dropdown";
import { ToastContext } from "../../../../services/contexts/toast-context";
import styled from "styled-components";

// -- Styled Components for Redesign --
const CardContainer = styled.div`
  min-width: 50vw;
  background-color: var(--surface-card, #ffffff);
  border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    min-width: 100%;
    padding: 16px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--surface-border, #f3f4f6);
  padding-bottom: 16px;
  
  h3 {
    margin: 0;
    font-size: 1.25rem;
    color: var(--text-color, #374151);
    font-weight: 600;
  }
`;

const GridLine = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  width: 100%;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;


const Experience = ({ defaultData = [],loading=false, onSave, onBack }) => {
  const toastRef = useContext(ToastContext);
  const [experiences, setExperiences] = useState(Array.isArray(defaultData) ? formatDateFields(defaultData) : []);
  const [experienceError, setExperienceError] = useState({});

  const schema = Yup.array().of(
    Yup.object({
      job_title: Yup.string().required("Job Title is required"),
      company: Yup.string().required("Company Name is required"),
      start_date: Yup.date().required("Start Date is required"),
      end_date: Yup.date().nullable(),
      city: Yup.string().required("City is required"),
      country: Yup.string().required("Country is required"),
      description: Yup.string().required("Job Description is required"),
    }));
  
  function formatDateFields(items) {
    return items.map(item => ({
      ...item,
      start_date: item.start_date ? (item.start_date.length === 4 ? new Date(`${item.start_date}-01-01`) : new Date(item.start_date)) : null,
      end_date: item.end_date ? (item.end_date.length === 4 ? new Date(`${item.end_date}-12-31`) : new Date(item.end_date)) : null,
    }));
  }

  const handleInputChange = (value, index, field) => {
    const newExperiences = [...experiences];
    newExperiences[index][field] = value;
    setExperiences(newExperiences);
  };

  const addExperience = () => {
    setExperiences([
      ...experiences,
      {
        job_title: "",
        company: "",
        start_date: null,
        end_date: null,
        city: "",
        country: "",
        description: "",
      },
    ]);
  };

  const removeExperience = (index) => {
    const newExperiences = experiences.filter((_, i) => i !== index);
    setExperiences(newExperiences);
  };

  const handleSubmitData = () => {
    handleSubmit(experiences, schema, setExperienceError, onSave);
    if (experienceError && Object.entries(experienceError).length != 0) {
      toastRef.current.show({ severity: 'error', summary: "Error", detail: formatErrors(experienceError), life: 6000 });
    }
    setExperienceError({})
  };

  return (
    <div style={{ padding: "20px 0" }}>
      {/* Add Experience Button */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
        <Button
          label="Add Experience"
          icon="pi pi-plus"
          outlined
          className="p-button-secondary"
          onClick={addExperience}
        />
      </div>

      {/* Experiences List */}
      <div style={{ display: "flex", flexDirection: "column", gap: "24px", width: "100%" }}>
        {experiences.map((experience, index) => (
          <CardContainer key={index}>
            <CardHeader>
              <h3>{experience.job_title ? experience.job_title : `Experience ${index + 1}`}</h3>
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-danger p-button-text"
                onClick={() => removeExperience(index)}
                aria-label="Remove Experience"
                style={{ padding: "8px" }}
              />
            </CardHeader>
            <Flex direction="column" align="stretch" gap="20px" style={{width: '100%'}}>
              
              <GridLine>
                <Input
                  value={experience.job_title}
                  label="Job Title"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "job_title")}
                  error={experienceError?.[index]?.job_title}
                />
                <Input
                  value={experience.company}
                  label="Company"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "company")}
                  error={experienceError?.[index]?.company}
                />
              </GridLine>

              <GridLine>
                <DatePicker
                  value={experience.start_date}
                  label="Start Date"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "start_date")}
                  error={experienceError?.[index]?.start_date}
                />
                <DatePicker
                  value={experience.end_date}
                  label="End Date"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "end_date")}
                  error={experienceError?.[index]?.end_date}
                />
              </GridLine>

              <GridLine>
                <Input
                  value={experience.city}
                  label="City"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "city")}
                  error={experienceError?.[index]?.city}
                />
                <CountryDropdown
                  value={experience.country || ""}
                  error={experienceError?.[index]?.country}
                  onChange={(value) => handleInputChange(value, index, "country")}
                />
              </GridLine>

              <div style={{ width: "100%" }}>
                <InputArea
                  value={experience.description}
                  label="Description & Responsibilities"
                  width="100%"
                  height="120px"
                  onChange={(value) => handleInputChange(value, index, "description")}
                  error={experienceError?.[index]?.description}
                />
              </div>

            </Flex>
          </CardContainer>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "24px" }}>
        <Button 
          label="Save & Continue" 
          icon="pi pi-check" 
          loading={loading} 
          disabled={loading} 
          className="p-button-primary" 
          onClick={handleSubmitData} 
        />
      </div>
    </div>
  );
};

export default Experience;
